<template>
    <div class="row mt-3">
        <div class="col">
            <label for="input" class="form-label">Please choose a small subset of data to be analyzed.
                (CSV/AVRO/PARQUET/JSON less than 5 MB)</label>
            <BrowseFile :generateURL="false" v-model="fileData" :acceptedFileTypes="['avro', 'csv', 'json', 'parquet']"
                :maxFileSize="5242880" @input="handleFileDataUpdate" />
        </div>
        <div class="col-2 btn-align">
            <button class="btn btn-primary w-100" @click="onGenerate"
                :disabled="loading || (!fileData.s3Path && !fileData.fileContent)">
                <div v-if="submitting" class="text-center">
                    <div class="spinner-border spinner-border-sm" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                </div>
                <span v-else>Generate</span>
            </button>
        </div>

        <div class="col-2 btn-align" v-if="response && response.files && $store.state.activeTab == 'Configuration'">
            <button class="btn btn-primary w-100"
                @click="showModal({ component: 'addJob', data: { files: configFiles, screenlarge: true } })">
                <span>Create Job</span>
            </button>
        </div>
    </div>
    <div class="row mt-3">
        <InputField :editable="!(loading || (!fileData.s3Path && !fileData.fileContent))" :label="'Custom Instructions'" type="textarea" v-model="customInstructions"
            :toolTip="'Custom instructions for generating the edge extract file'" />
    </div>
    <tabs :tabs="tabItems" v-if="submitted">
        <template v-slot:edgeExtractAnalyse>
            <div class="mt-3" id="">
                <div v-if="error" class="alert alert-danger mb-3">
                    {{ error }}
                </div>
                <div v-if="!textResponse && !error" class="row">
                    <p>Analysing input file... </p>
                    <div class="loading-content-card">
                        <div class="content">
                            <h2></h2>
                            <p></p>
                        </div>
                    </div>
                </div>
                <div v-else class="row">
                    <div class="col-4">
                        <div class="card">
                            <div class='card-body'>
                                <Markdown :content="textResponse" />
                            </div>
                        </div>
                    </div>
                    <div class="col-8">
                        <div v-if="!graphResponse && textResponse">
                            <p>Creating Graph Visualiser... </p>
                            <div class="loading-content-card">
                                <div class="content">
                                    <h2></h2>
                                    <p></p>
                                </div>
                            </div>
                        </div>
                        <GraphVisualizer v-else :data="graphResponse" />
                    </div>
                </div>
            </div>
        </template>
        <template v-slot:edgeExtractConfig>
            <div class="mt-3" id="">
                <div v-if="error" class="alert alert-danger mb-3">
                    {{ error }}
                </div>
                <div class="row">
                    <div class="col">
                        <div v-if="!response || !response.files">
                            <p>Creating Edge Extract Configuraton... </p>
                            <div class="loading-content-card">
                                <div class="content">
                                    <h2></h2>
                                    <p></p>
                                </div>
                            </div>
                        </div>
                        <job-editor v-model="yamlContent" :activeTab="activeTab"></job-editor>
                    </div>
                </div>
            </div>
        </template>

    </tabs>
</template>

<script>
import Tabs from "../../components/Tabs.vue";
import Markdown from "../../components/Markdown.vue";
import JobEditor from "../../components/sandbox/JobEditor.vue";
import BrowseFile from "../../components/BrowseFile.vue";
import * as d3 from "d3";
import { mapActions } from "vuex";
import GraphVisualizer from "../../components/GraphVisualizer.vue";
import InputField from "../../components/InputField.vue";
import untruncateJson from "untruncate-json";
export default {
    name: "GraphMyDataAnalyser",
    components: { Tabs, Markdown, JobEditor, BrowseFile, GraphVisualizer, InputField },
    data() {
        return {
            fileData: {
                s3Path: null,
                localFile: null,
                fileContent: null,
                pathError: '',
                isFileSelected: false
            },
            configFiles: [],
            response: null,
            submitted: false,
            submitting: false,
            error: null,
            activeTab: null,
            loading: false,
            tabItems: [
                {
                    name: "Analysis",
                    id: "id1",
                    slot: "edgeExtractAnalyse"
                },
                {
                    name: "Configuration",
                    id: "id2",
                    slot: "edgeExtractConfig"
                }
            ],
            fileType: 'csv',
            fileName: '',
            graphResponse: null,
            customInstructions:''
        }
    },
    watch: {
        "response.files": {
            handler: function (newFiles) {
                //change key from name to relative_path
                if (newFiles && Array.isArray(newFiles)) {
                    this.configFiles = newFiles.map((file) => {
                        return {
                            relative_path: file.name,
                            content: file.content
                        }
                    });
                } else {
                    this.configFiles = [];
                }
            },
            deep: true
        }
    },
    computed: {
        textResponse() {
            if (this.response && (this.response.text_response))
                return (this.response.text_response)
            return null
        },
        yamlContent() {
            const files = {}
            if (this.response && this.response.files && this.response.files.length) {
                for (const file of this.response.files) {
                    files[file.name] = file.content ? file.content : ""
                }
            }
            return files
        }
    },
    methods: {
        untruncateJson,
        ...mapActions({
            showModal: "showModal",
            graphMyData: "amdp/graphMyData",
            fetchOutputParser: "amdp/fetchOutputParser"
        }),

        handleFileDataUpdate(newFileData) {
            this.fileData = newFileData;
        },
        convertFileToBase64(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                    // Remove the data URL prefix (data:application/octet-stream;base64,)
                    const base64 = reader.result.split(',')[1];
                    resolve(base64);
                };
                reader.onerror = reject;
                reader.readAsDataURL(file);
            });
        },
        async onGenerate() {
            this.submitting = true;
            this.submitted = true;
            this.error = null;
            let tempActiveTab = "";
            this.response = null;
            this.loading = true;
            this.graphResponse = null;
            let graphResponse = null;
            this.fileType = this.getFileType(this.fileData.s3Path, this.fileData.localFile)
            try {
                let payload = null;
                let response = null;
                let flag = true;
                // for csv and json file from s3 path
                if (this.fileData.s3Path && (this.fileType === 'csv' || this.fileType === 'json')) {
                    payload = {
                        s3_path: this.fileData.s3Path,
                        file_name: this.fileName,
                        file_type: this.fileType,
                        presign_url: this.fileData.presignedUrl.trim() || null
                    };
                }
                // for avro and parquet files from s3 path or local file
                else if (this.fileType === 'avro' || this.fileType === 'parquet') {
                    flag = await this.parseS3File().then((res) => {
                        if (res) {
                            payload = {
                                file_name: this.fileName,
                                file_content: JSON.stringify(this.fileContent),
                                file_type: this.fileType
                            };
                        }
                        return res
                    })
                }
                // for local files (csv, json, avro, parquet)
                else if (this.fileData.localFile && (this.fileType === 'csv' || this.fileType === 'json')) {
                    const base64Data = await this.convertFileToBase64(this.fileData.localFile);
                    payload = {
                        file_content: base64Data,
                        file_name: this.fileName,
                        file_type: this.fileType
                    };
                } else {
                    throw new Error('No valid file selected');
                }
                if (payload && flag) {
                    if(this.customInstructions != ''){
                        payload.custom_instructions = this.customInstructions;
                    }
                    response = await this.graphMyData(payload)

                    // Check for 401 status code
                    if (response.status === 401) {
                        this.error = "Authentication failed. Please check your credentials and try again.";
                        throw new Error("Authentication failed (401)");
                    }

                    // Check for other error status codes (fetch API)
                    if (!response.ok) {
                        this.error = `Server returned error: ${response.status} ${response.statusText}`;
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }
                    const reader = response.body.getReader();
                    const decoder = new TextDecoder();
                    let buffer = '';
                    let loop = true;

                    while (loop) {
                        this.error = '';
                        const { value, done } = await reader.read();
                        if (done) {
                            break;
                        }
                        buffer += decoder.decode(value, { stream: true });

                        const lines = buffer.split('\n');
                        buffer = lines.pop() || '';

                        for (const line of lines) {
                            if (line.startsWith('data: ')) {
                                try {
                                    const data = JSON.parse(this.untruncateJson(line.slice(6)));
                                    if (data && typeof data === 'object' && (data.text_response || data.files)) {
                                        this.response = data;
                                        if (!this.activeTab && data.files && data.files.length && data.files[0].name) {
                                            if (tempActiveTab == data.files[0].name)
                                                this.activeTab = tempActiveTab;
                                            else
                                                tempActiveTab = data.files[0].name;
                                        }
                                    }
                                    if (data && (data.graph_response)) {
                                        graphResponse = data.graph_response;
                                    }
                                } catch (error) {
                                    this.error = `Error: ${error.message || "Error parsing SSE data"}`;
                                }
                            }
                        }
                    }
                }
            } catch (error) {
                this.error = `Error: ${error.message || "Failed to execute graph analysis"}`;
            } finally {
                this.submitting = false;
                this.loading = false;
                this.graphResponse = graphResponse
            }
        },
        getFileType(s3Name, localFileName) {
            if (s3Name)
                this.fileName = s3Name.split('/').pop();
            else
                this.fileName = localFileName.name;
            return this.fileName.split('.').pop();
        },
        async parseS3File() {
            this.error = null;
            let payload = {}
            if (this.fileData.fileContent) {
                const base64Data = await this.convertFileToBase64(this.fileData.localFile)
                payload.file_content = base64Data;
                payload.file_type = this.fileType;
            } else {
                payload.cid = this.$store.state.currentClient.cid;
                payload.sandbox_name = this.fileData.sandbox;
                payload.path = this.fileData.s3Path;
            }
            this.loading = true;
            let flag = await this.fetchOutputParser(payload,).then((response) => {
                let outputData = []
                if (response.data) {
                    outputData = response.data
                }
                this.fileContent = outputData;
                return true
            }).catch(error => {
                this.error = error.response.data.error;
                return false
            }).finally(() => {
                this.loading = false
            })
            return flag
        }
    },
    mounted() {
        this.$store.dispatch('setActiveTab', "Analysis");
    }
};
</script>

<style>
.btn-align {
    margin-top: 31px;
}

@keyframes shine {
    to {
        background-position-x: -200%;
    }
}

.loading-content-card {
    margin: 0px;
    background: #fff;
    border-radius: 5px;

    .image,
    h2,
    p {
        background: #eee;
        background: linear-gradient(110deg, #ececec 8%, #f5f5f5 18%, #ececec 33%);
        border-radius: 5px;
        background-size: 200% 100%;
        animation: 1.5s shine linear infinite;
    }

    .content {
        padding: 15px;
    }

    .image {
        height: 200px;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
    }

    h2 {
        height: 30px;
    }

    p {
        height: 70px;
    }
}
</style>